import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form, message } from 'antd';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCanCheck } from '@/ability/helpers';
import { PROJECT_BLOCKERS_VIEW, PROJECT_BLOCKERS_CREATE, PROJECT_BLOCKERS_UPDATE } from '@/ability';
import { useProjectMilestones } from '@hooks/projects/useProjectMilestones';
import { useProjectSprints } from '@hooks/projects/useProjectSprints';
import { useBlockerDiscussions } from '@hooks/projects/discussion/useBlockerDiscussions';
import { contentToApiMentions } from '@utils/richTextMentions';
import {
    blockerResolvedPatchBody,
    buildTaskDescription,
    partitionBlockersByStatus,
    truncateFileName,
} from '@hooks/projects/projectHelpers';
import {
    getProjectBlockers,
    createProjectBlocker,
    updateProjectBlocker,
    createProjectTaskBlocker,
    updateProjectTaskBlocker,
} from '@services/projects.service';
import { invalidateProjectQueries } from '@hooks/projects/projectQueryInvalidation';
import { useProjectDetail } from '@hooks/projects/detail/useProjectDetail';

export const PROJECT_BLOCKERS_QUERY_KEY = (projectId) => ['projects', projectId, 'blockers'];

const MENTION_USERS_LIMIT = 100;

/** Kanban column ids (dnd-kit). */
export const BLOCKER_KANBAN_COL = { ACTIVE: 'active', RESOLVED: 'resolved' };

// =========================================================
// Queries + mutations
// =========================================================
export function useProjectBlockers(projectId) {
    const queryClient = useQueryClient();
    const can = useCanCheck();
    const canView = can(PROJECT_BLOCKERS_VIEW);

    const { data: project } = useProjectDetail(projectId, { enabled: !!projectId });
    const useTasksEnabled = project?.capabilities?.useTasks !== false;

    const {
        data: blockers = [],
        isLoading,
        isError,
        refetch,
    } = useQuery({
        queryKey: PROJECT_BLOCKERS_QUERY_KEY(projectId),
        queryFn: () => getProjectBlockers(projectId),
        enabled: !!projectId && canView && useTasksEnabled,
    });

    const createMutation = useMutation({
        mutationFn: (body) => {
            const files = body.files;
            const linkAttachments = Array.isArray(body.attachments) ? body.attachments : [];
            if (files?.length) {
                const formData = new FormData();
                formData.append('title', typeof body.title === 'string' ? body.title : '');
                if (body.description) formData.append('description', JSON.stringify(body.description));
                if (linkAttachments.length > 0) formData.append('attachmentUrls', JSON.stringify(linkAttachments));
                files.forEach((file) => formData.append('files', file));
                return createProjectBlocker(projectId, formData);
            }
            const { files: _f, attachments: _a, ...rest } = body;
            return createProjectBlocker(projectId, { ...rest, ...(linkAttachments.length > 0 && { attachments: linkAttachments }) });
        },
        onSuccess: () => {
            invalidateProjectQueries(queryClient, projectId);
        },
    });

    const createBlocker = (body, callbacks = {}) => {
        createMutation.mutate(body, {
            onSuccess: callbacks.onSuccess,
            onError: callbacks.onError,
        });
    };

    const updateMutation = useMutation({
        mutationFn: ({ blockerId, body }) => updateProjectBlocker(projectId, blockerId, body),
        onSuccess: () => {
            invalidateProjectQueries(queryClient, projectId);
        },
    });

    const updateBlocker = (blockerId, body, callbacks = {}) => {
        if (!blockerId) return;
        updateMutation.mutate(
            { blockerId, body },
            {
                onSuccess: callbacks.onSuccess,
                onError: callbacks.onError,
            },
        );
    };

    return {
        blockers: Array.isArray(blockers) ? blockers : [],
        isLoading,
        isError,
        refetch,
        createBlocker,
        createBlockerPending: createMutation.isPending,
        updateBlocker,
        updateBlockerPending: updateMutation.isPending,
        canView,
    };
}

// =========================================================
// Task-specific Queries + mutations
// =========================================================
export function useProjectTaskBlockers(projectId, taskId) {
    const queryClient = useQueryClient();

    const createMutation = useMutation({
        mutationFn: (body) => createProjectTaskBlocker(projectId, taskId, body),
        onSuccess: () => {
            invalidateProjectQueries(queryClient, projectId);
            if (taskId) {
                // optionally invalidate specific task queries
            }
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({ blockerId, body }) => updateProjectTaskBlocker(projectId, taskId, blockerId, body),
        onSuccess: () => {
            invalidateProjectQueries(queryClient, projectId);
        },
    });

    const createBlocker = (body, callbacks = {}) => {
        createMutation.mutate(body, {
            onSuccess: callbacks.onSuccess,
            onError: callbacks.onError,
        });
    };

    const updateBlocker = (blockerId, body, callbacks = {}) => {
        if (!blockerId) return;
        updateMutation.mutate(
            { blockerId, body },
            {
                onSuccess: callbacks.onSuccess,
                onError: callbacks.onError,
            },
        );
    };

    return {
        createBlocker,
        createBlockerAsync: createMutation.mutateAsync,
        createBlockerPending: createMutation.isPending,
        updateBlocker,
        updateBlockerAsync: updateMutation.mutateAsync,
        updateBlockerPending: updateMutation.isPending,
    };
}

// =========================================================
// Derived board state
// =========================================================

/**
 * Project-level blockers (no milestone/sprint scope).
 */
export function useProjectBlockersBoard(projectId) {
    const {
        blockers,
        isLoading: blockersLoading,
        createBlocker,
        createBlockerPending,
        updateBlocker,
        updateBlockerPending,
    } = useProjectBlockers(projectId);

    const { activeBlockers, resolvedBlockers } = useMemo(() => partitionBlockersByStatus(blockers), [blockers]);

    return {
        activeBlockers,
        resolvedBlockers,
        isLoading: blockersLoading,
        createBlocker,
        createBlockerPending,
        updateBlocker,
        updateBlockerPending,
    };
}

// =========================================================
// Create modal state
// =========================================================

/**
 * Add-blocker modal: pending file/link attachments, @mentions, and submit (same pattern as sprint/task create).
 */
export function useProjectBlockerCreateModal(projectId, { project, createBlocker, createBlockerPending }) {
    const [form] = Form.useForm();
    const [modalOpen, setModalOpen] = useState(false);
    const [blockerPendingAttachments, setBlockerPendingAttachments] = useState([]);
    const [pendingLinkAttachments, setPendingLinkAttachments] = useState([]);
    const [linkModalOpen, setLinkModalOpen] = useState(false);
    const [linkModalForm] = Form.useForm();
    const linkModalCallbackRef = useRef(null);

    const { milestones } = useProjectMilestones(projectId);
    const { sprints } = useProjectSprints(projectId);

    const mentionUsers = useMemo(() => (project?.assignedUsers ?? []).slice(0, MENTION_USERS_LIMIT), [project?.assignedUsers]);
    const mentionMilestones = useMemo(() => (Array.isArray(milestones) ? milestones : []), [milestones]);
    const mentionSprints = useMemo(() => (Array.isArray(sprints) ? sprints : []), [sprints]);

    useEffect(() => {
        if (!modalOpen) {
            setBlockerPendingAttachments([]);
            setPendingLinkAttachments([]);
        }
    }, [modalOpen]);

    const makeRemovePending = useCallback(
        (setPending) => {
            return (id) => {
                setPending((prev) => {
                    const index = prev.findIndex((p) => p.id === id);
                    if (index < 0) return prev.filter((p) => p.id !== id);
                    let content = form.getFieldValue('description') || '';
                    const linkRegex = new RegExp(`<a\\s(?=[^>]*href="#attachment-${index}")[^>]*>[\\s\\S]*?</a>`, 'gi');
                    content = content.replace(linkRegex, '');
                    for (let j = prev.length - 1; j > index; j--) {
                        content = content.replace(new RegExp(`#attachment-${j}`, 'g'), `#attachment-${j - 1}`);
                    }
                    form.setFieldValue('description', content);
                    return prev.filter((p) => p.id !== id);
                });
            };
        },
        [form],
    );

    const handleBlockerFilesSelected = useCallback((files) => {
        if (!files?.length) return;
        const next = files.map((file, i) => ({
            id: `blocker-${Date.now()}-${i}`,
            file,
            name: file.name,
            type: file.type,
        }));
        setBlockerPendingAttachments((prev) => [...prev, ...next]);
    }, []);

    const removeBlockerAttachment = useCallback((id) => makeRemovePending(setBlockerPendingAttachments)(id), [makeRemovePending]);

    const handleLinkRequest = useCallback(
        (insertLink) => {
            linkModalCallbackRef.current = insertLink;
            linkModalForm.resetFields();
            setLinkModalOpen(true);
        },
        [linkModalForm],
    );

    const handleLinkModalSubmit = useCallback(() => {
        linkModalForm.validateFields().then((values) => {
            const url = (values.url || '').trim();
            const title = (values.title || '').trim();
            if (!url) {
                message.warning('Enter URL');
                return;
            }
            const insertLink = linkModalCallbackRef.current;
            if (insertLink) insertLink({ url, title: title || url });
            setPendingLinkAttachments((prev) => [...prev, { id: `link-${Date.now()}`, url, title: title || url }]);
            setLinkModalOpen(false);
            linkModalCallbackRef.current = null;
        });
    }, [linkModalForm]);

    const openModal = useCallback(() => setModalOpen(true), []);

    const closeModal = useCallback(() => {
        setModalOpen(false);
        form.resetFields();
    }, [form]);

    const handleCreateSubmit = useCallback(() => {
        form.validateFields().then((values) => {
            const descriptionHtml = contentToApiMentions(values.description ?? '');
            const description = buildTaskDescription(descriptionHtml);
            createBlocker(
                {
                    title: (values.title ?? '').trim() || 'Blocker',
                    ...(description && { description }),
                    ...(blockerPendingAttachments.length > 0 && { files: blockerPendingAttachments.map((p) => p.file) }),
                    ...(pendingLinkAttachments.length > 0 && {
                        attachments: pendingLinkAttachments.map(({ url, title }) => ({ url, title })),
                    }),
                },
                {
                    onSuccess: () => {
                        message.success('Blocker added');
                        closeModal();
                    },
                    onError: (err) => message.error(err?.response?.data?.message || err?.message || 'Failed to add blocker'),
                },
            );
        });
    }, [form, createBlocker, closeModal, blockerPendingAttachments, pendingLinkAttachments]);

    return {
        form,
        modalOpen,
        openModal,
        closeModal,
        handleCreateSubmit,
        createBlockerPending,
        blockerPendingAttachments,
        pendingLinkAttachments,
        handleBlockerFilesSelected,
        removeBlockerAttachment,
        handleLinkRequest,
        handleLinkModalSubmit,
        linkModalOpen,
        setLinkModalOpen,
        linkModalForm,
        linkModalCallbackRef,
        mentionUsers,
        mentionMilestones,
        mentionSprints,
        truncateFileName,
    };
}

// =========================================================
// Kanban drag/drop
// =========================================================

/**
 * Native drag/drop between Active and Resolved blocker columns; calls PATCH blocker when dropped.
 */
export function useProjectBlockersKanbanDrag({ updateBlocker }) {
    const [dropTarget, setDropTarget] = useState(null);
    const [draggingColumn, setDraggingColumn] = useState(null);

    const clearDropTarget = useCallback(() => {
        setDropTarget(null);
        setDraggingColumn(null);
    }, []);

    const handleBlockerMove = useCallback(
        ({ blockerId, sourceColumn, targetColumn }) => {
            clearDropTarget();
            if (!blockerId || !sourceColumn || !targetColumn) return;
            if (sourceColumn === targetColumn) return;

            const onErr = (err) =>
                message.error(err?.response?.data?.message || err?.message || 'Failed to update blocker');

            if (sourceColumn === BLOCKER_KANBAN_COL.ACTIVE && targetColumn === BLOCKER_KANBAN_COL.RESOLVED) {
                updateBlocker(blockerId, blockerResolvedPatchBody(true), {
                    onSuccess: () => message.success('Marked as resolved'),
                    onError: onErr,
                });
                return;
            }
            if (sourceColumn === BLOCKER_KANBAN_COL.RESOLVED && targetColumn === BLOCKER_KANBAN_COL.ACTIVE) {
                updateBlocker(blockerId, blockerResolvedPatchBody(false), {
                    onSuccess: () => message.success('Moved to active'),
                    onError: onErr,
                });
                return;
            }
            message.warning('This move is not supported');
        },
        [updateBlocker, clearDropTarget],
    );

    const handleDndDragStart = useCallback((payload) => {
        setDraggingColumn(payload?.sourceColumn ?? null);
    }, []);

    const handleDndDragOverColumn = useCallback((columnId) => {
        setDropTarget(columnId);
    }, []);

    const handleDndDragEnd = useCallback(
        ({ overColumnId, payload }) => {
            handleBlockerMove({
                blockerId: payload?.blockerId,
                sourceColumn: payload?.sourceColumn,
                targetColumn: overColumnId ?? null,
            });
        },
        [handleBlockerMove],
    );

    return {
        dropTarget,
        draggingColumn,
        handleDndDragStart,
        handleDndDragOverColumn,
        handleDndDragEnd,
        clearDropTarget,
    };
}

// =========================================================
// Tab orchestration hook
// =========================================================

/**
 * Blockers tab: board, create modal, Kanban drag, detail modal + discussion (thin page orchestration).
 */
export function useProjectBlockersTab(projectId, project) {
    const can = useCanCheck();
    const canCreateBlocker = can(PROJECT_BLOCKERS_CREATE);
    const canUpdateBlocker = can(PROJECT_BLOCKERS_UPDATE);
    const canDragBlockers = canUpdateBlocker || canCreateBlocker;

    const board = useProjectBlockersBoard(projectId);

    const blockerModal = useProjectBlockerCreateModal(projectId, {
        project,
        createBlocker: board.createBlocker,
        createBlockerPending: board.createBlockerPending,
    });

    const kanbanDrag = useProjectBlockersKanbanDrag({ updateBlocker: board.updateBlocker });

    const [selectedBlocker, setSelectedBlocker] = useState(null);

    const discussionsHook = useBlockerDiscussions(projectId, selectedBlocker?.id);

    const openBlockerDetail = useCallback((blocker) => {
        setSelectedBlocker(blocker ?? null);
    }, []);

    const closeBlockerDetail = useCallback(() => setSelectedBlocker(null), []);

    return {
        ...board,
        blockerModal,
        ...kanbanDrag,
        selectedBlocker,
        openBlockerDetail,
        closeBlockerDetail,
        blockerDiscussions: discussionsHook.discussions,
        blockerDiscussionsLoading: discussionsHook.isLoading,
        createBlockerPost: discussionsHook.createPost,
        createBlockerPostPending: discussionsHook.createPostPending,
        editBlockerPost: discussionsHook.editPost,
        editBlockerPostPending: discussionsHook.editPostPending,
        deleteBlockerPost: discussionsHook.deletePost,
        deleteBlockerPostPending: discussionsHook.deletePostPending,
        canCreateBlocker,
        canDragBlockers,
    };
}
